import React from 'react';
import { Trash2, Lock } from 'lucide-react'; // Icons
import { QUIZ_VIEW_TYPES, QUIZ_TYPE_LABELS, microGroupOf, microGroupNames } from '../utils/Constants';
import { AddQuizCard } from './UserQuizAddForm';

const STATUS_STYLE = {
    completed: { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
    'in-progress': { bg: '#fef9c3', color: '#92400e', label: 'In Progress (Test running in background)' },
    default: { bg: '#dbeafe', color: '#1e3a8a', label: 'No Submission Yet' },
};

// --- QuizGrid Component (Displays a list of quizzes) ---
export const QuizGrid = ({ quizzes, quizzesSubmissionDetails, title, onDelete, onViewDetails, onAddQuiz, quizViewType, level, microGroupFilter }) => {
    const isMyQuizView = quizViewType === QUIZ_VIEW_TYPES.MY_QUIZ;
    // Published & Micro pages drop the separate "Attempted" section — every quiz shows in one
    // grid, its status conveyed by the per-card color badge (completed / in-progress / none).
    const isPublished = quizViewType === QUIZ_VIEW_TYPES.PUBLISHED_QUIZ;
    const isMicro = quizViewType === QUIZ_VIEW_TYPES.MICRO_QUIZ;
    const isCompetition = quizViewType === QUIZ_VIEW_TYPES.COMPETITION_QUIZ;
    const flat = isPublished || isMicro;

    let safeQuizzes = quizzes || [];
    // Previous-year bank: show newest exam years first.
    if (quizViewType === QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ) {
        safeQuizzes = [...safeQuizzes].sort((a, b) => (b.year || 0) - (a.year || 0));
    }

    const renderCard = (quiz) => {
        const details = quizzesSubmissionDetails[quiz.quiz_id];
        const status = details?.submission_status;
        const style = STATUS_STYLE[status] || STATUS_STYLE.default;
        // Locked-but-visible: this quiz is beyond the user's plan allotment. Show it (so users know
        // it exists) but greyed + non-clickable, with an "upgrade to unlock" prompt. The backend
        // also rejects taking/opening it, so this is display-only.
        const locked = !!quiz.locked;
        return (
            <div key={quiz.quiz_id} className={locked ? undefined : 'kp-quiz-card'} style={{
                padding: '1rem',
                border: `1px solid ${locked ? '#e5d5f5' : '#d1d5db'}`,
                borderRadius: '0.5rem',
                backgroundColor: locked ? '#f6f3fb' : '#f9fafb',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: locked ? 'not-allowed' : 'pointer', // Indicate clickable card
            }}>
                <div
                    onClick={locked ? undefined : () => onViewDetails(quiz)}
                    style={{ flexGrow: 1, opacity: locked ? 0.65 : 1 }}
                > {/* Clickable area for details (disabled when locked) */}
                    <h4 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        {locked && <Lock size={15} style={{ color: '#7c3aed', flexShrink: 0 }} />}
                        <span>Title: {quiz.title}</span>
                    </h4>
                    <p style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.5rem' }}>Description: {quiz.description}</p>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        <span>Level: {quiz.level}</span> | <span>Type: {QUIZ_TYPE_LABELS[quiz.quiz_type] || quiz.quiz_type}</span>
                        {quiz.division ? <> | <span>Division: {quiz.division}</span></> : null}
                        {quiz.unit ? <> | <span>Unit: {quiz.unit}</span></> : null}
                        {quiz.year ? <> | <span style={{ fontWeight: 600, color: '#4c1d95' }}>Exam Year: {quiz.year}</span></> : null}
                        {quiz.board ? <> | <span>Board: {quiz.board}</span></> : null}
                        {quiz.subject ? <> | <span>Subject: {quiz.subject}</span></> : null}
                        {quiz.total_questions ? <> | <span>Questions: {quiz.total_questions}</span></> : null}
                        {quiz.duration_minutes ? <> | <span>Time: {quiz.duration_minutes} min</span></> : null}
                    </div>
                    {!locked && (
                    <div style={{ marginBottom: '0.25rem', marginTop: '0.5rem' }}>
                        <span style={{
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '9999px',
                            backgroundColor: style.bg,
                            color: style.color,
                            display: 'inline-block'
                        }}>
                            {style.label}
                        </span>
                        {status === 'completed' && details?.score != null && (
                            <span style={{ fontSize: '0.75rem', color: '#065f46', fontWeight: 600, marginLeft: '0.5rem' }}>
                                Score: {details.score}
                            </span>
                        )}
                    </div>
                    )}
                </div>
                {locked ? (
                    <div style={{
                        marginTop: '0.6rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#6d28d9',
                        background: 'linear-gradient(90deg,#ede9fe,#f5f3ff)',
                        border: '1px solid #ddd6fe',
                        borderRadius: '0.4rem',
                        padding: '0.4rem 0.6rem',
                    }}>
                        <Lock size={14} style={{ flexShrink: 0 }} />
                        Upgrade your package to unlock this test.
                    </div>
                ) : status !== 'completed' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {onDelete && (
                        <button
                            onClick={() => onDelete(quiz.quiz_id)}
                            style={{
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                padding: '0.4rem',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Trash2 style={{ width: '1rem', height: '1rem' }} />
                        </button>
                    )}
                </div>
                )}
            </div>
        );
    };

    // Padding gives the hover lift (translateY) + purple aura room so `overflowY: auto` doesn't
    // clip the top/sides/bottom of a raised card.
    const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', overflowY: 'auto', padding: '10px 6px 16px' };

    // Eye-catching group heading for a micro section/topic name. A colored gradient chip with a
    // left accent bar so the group label (the section or topic) reads as the primary structure of
    // the page rather than a faint sub-heading.
    const renderGroupHeading = (label) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0 0 0.9rem' }}>
            <span style={{ width: '6px', alignSelf: 'stretch', minHeight: '1.9rem', borderRadius: '9999px', background: 'linear-gradient(180deg,#6d28d9,#4338ca)' }} />
            <span style={{
                fontSize: '1.15rem',
                fontWeight: 800,
                letterSpacing: '0.01em',
                color: '#4338ca',
                background: 'linear-gradient(90deg,#ede9fe,#f5f3ff)',
                border: '1px solid #ddd6fe',
                borderRadius: '9999px',
                padding: '0.35rem 1rem',
                textTransform: 'uppercase',
                boxShadow: '0 1px 2px rgba(76,29,149,0.08)',
            }}>
                {label}
            </span>
        </div>
    );

    const renderBody = () => {
        // Micro (any level): group by section (IELTS/TOEFL/GRE) or topic (other levels). A chosen
        // "Find Quiz By" group narrows the view to that one group.
        if (isMicro) {
            let names = microGroupNames(safeQuizzes, level);
            if (microGroupFilter && microGroupFilter !== 'All') {
                names = names.filter(n => n === microGroupFilter);
            }
            const byCat = {};
            safeQuizzes.forEach(q => {
                const c = microGroupOf(q);
                (byCat[c] = byCat[c] || []).push(q);
            });
            const anything = names.some(c => (byCat[c] || []).length > 0);
            if (!anything) {
                return <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No tests found for this category.</div>;
            }
            return names.map(cat => {
                const items = byCat[cat] || [];
                if (!items.length) return null;
                return (
                    <div key={cat} style={{ marginBottom: '1.75rem' }}>
                        {renderGroupHeading(cat)}
                        <div style={gridStyle}>{items.map(renderCard)}</div>
                    </div>
                );
            });
        }

        // Published (non-micro): single flat grid, no Attempted section.
        if (flat) {
            if (!safeQuizzes.length) {
                return <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>No tests found for this category.</div>;
            }
            return <div style={gridStyle}>{safeQuizzes.map(renderCard)}</div>;
        }

        // My Quiz: one grid with the Add card + ALL the user's tests (no separate "Attempted"
        // section) — completed ones stay distinguishable by their green "Completed" badge.
        if (isMyQuizView) {
            return (
                <div style={gridStyle}>
                    <AddQuizCard onClick={onAddQuiz} />
                    {safeQuizzes.map(renderCard)}
                </div>
            );
        }

        // Everything else (Competition, Daily, Previous Year): one flat grid — no separate
        // "Attempted Tests" section. Each card's status badge (completed / in-progress / none)
        // distinguishes attempted tests from the rest.
        if (!safeQuizzes.length) {
            return (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    {isCompetition ? 'No Competition Going On Currently' : 'No tests found for this category.'}
                </div>
            );
        }
        return <div style={gridStyle}>{safeQuizzes.map(renderCard)}</div>;
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            padding: '1rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            backgroundColor: '#ffffff',

        }}>
            {!isMicro && !isPublished && !isCompetition && !isMyQuizView && quizViewType !== QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ && <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>{title}</h3>}
            {renderBody()}
        </div>
    );
};
